import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LoadingProvider } from './context/LoadingContext';
import { modernTheme } from './theme/modernTheme';
import GlobalLoader from './components/GlobalLoader';

// Pages
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Users from './pages/Users';
import UserFormPage from './pages/UserFormPage';
import Profile from './pages/Profile';
import Contacts from './pages/Contacts';
import BulkUploadPage from './pages/BulkUploadPage';
import ContactDetailPage from './pages/ContactDetailPage';
import Donations from './pages/Donations';
import DonationPledgePage from './pages/DonationPledgePage';
import Campaigns from './pages/Campaigns';
import CampaignDetailPage from './pages/CampaignDetailPage';
import Transaction from './pages/Transaction';
import TransactionDetailPage from './pages/TransactionDetailPage';
import EmailTemplates from './pages/EmailTemplates';
import EmailTemplateEditor from './pages/EmailTemplateEditor';
import Tags from './pages/Tags';
import DonationSchemas from './pages/DonationSchemas';
import Reminders from './pages/Reminders';
import ReminderDetailPage from './pages/ReminderDetailPage';
import UnsubscribePage from './pages/UnsubscribePage';
import Layout from './components/Layout';
import WhatsAppTemplates from './pages/WhatsAppTemplates';
import WhatsAppTemplateDetailPage from './pages/WhatsAppTemplateDetailPage';
import WhatsAppTemplateFormPage from './pages/WhatsAppTemplateFormPage';
import TrustMasters from './pages/TrustMasters';
import TrustMasterDetailPage from './pages/TrustMasterDetailPage';
import TrustMasterEditPage from './pages/TrustMasterEditPage';
import TrustMasterNewPage from './pages/TrustMasterNewPage';
import NotFound from './pages/NotFound';
import PermissionRoute from './components/PermissionRoute';
import { useDispatch, useSelector } from 'react-redux';
import { fetchTags, selectGlobalTagsStatus } from './store/slices/tagsSlice';
import {
  fetchDonationSchemas,
  selectDonationSchemasStatus,
} from './store/slices/donationSchemasSlice';

// Modern theme is imported from theme/modernTheme.js

import LoadingPage from './components/LoadingPage';

const PrivateRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <LoadingPage />;
  }

  return isAuthenticated ? children : <Navigate to="/login" />;
};

function AppRoutes() {
  const dispatch = useDispatch();
  const tagsStatus = useSelector(selectGlobalTagsStatus);
  const donationSchemasStatus = useSelector(selectDonationSchemasStatus);
  const { isAuthenticated } = useAuth();

  React.useEffect(() => {
    // Only fetch tags  when user is authenticated and status is idle
    if (isAuthenticated && tagsStatus === 'idle') {
      dispatch(fetchTags({ limit: 10000 }));
    }
  }, [isAuthenticated, tagsStatus, dispatch]);

  React.useEffect(() => {
    // Only fetch donation schemas when user is authenticated and status is idle
    if (isAuthenticated && donationSchemasStatus === 'idle') {
      dispatch(fetchDonationSchemas());
    }
  }, [isAuthenticated, donationSchemasStatus, dispatch]);

  return (
    <Router>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/unsubscribe" element={<UnsubscribePage />} />
        <Route
          path="/"
          element={
            <PrivateRoute>
              <Layout />
            </PrivateRoute>
          }
        >
          <Route index element={<Dashboard />} />
          <Route path="users" element={<Users />} />
          <Route path="users/new" element={<UserFormPage />} />
          <Route path="users/:id" element={<UserFormPage />} />
          <Route path="profile" element={<Profile />} />
          <Route
            path="contacts"
            element={
              <PermissionRoute>
                <Contacts />
              </PermissionRoute>
            }
          />
          <Route
            path="contacts/import"
            element={
              <PermissionRoute>
                <BulkUploadPage />
              </PermissionRoute>
            }
          />
          <Route
            path="contacts/:id"
            element={
              <PermissionRoute>
                <ContactDetailPage />
              </PermissionRoute>
            }
          />
          <Route
            path="donations"
            element={
              <PermissionRoute>
                <Donations />
              </PermissionRoute>
            }
          />
          <Route
            path="donations/new"
            element={
              <PermissionRoute>
                <DonationPledgePage />
              </PermissionRoute>
            }
          />
          <Route
            path="donations/:id"
            element={
              <PermissionRoute>
                <DonationPledgePage />
              </PermissionRoute>
            }
          />
          <Route
            path="campaigns"
            element={
              <PermissionRoute>
                <Campaigns />
              </PermissionRoute>
            }
          />
          <Route
            path="campaigns/:id"
            element={
              <PermissionRoute>
                <CampaignDetailPage />
              </PermissionRoute>
            }
          />
          <Route path="transactions" element={<Transaction />} />
          <Route
            path="transactions/new"
            element={
              <PermissionRoute>
                <TransactionDetailPage />
              </PermissionRoute>
            }
          />
          <Route
            path="transactions/:id"
            element={
              <PermissionRoute>
                <TransactionDetailPage />
              </PermissionRoute>
            }
          />
          <Route
            path="email-templates"
            element={
              <PermissionRoute>
                <EmailTemplates />
              </PermissionRoute>
            }
          />
          <Route
            path="email-templates/new"
            element={
              <PermissionRoute>
                <EmailTemplateEditor />
              </PermissionRoute>
            }
          />
          <Route
            path="email-templates/:id"
            element={
              <PermissionRoute>
                <EmailTemplateEditor />
              </PermissionRoute>
            }
          />
          <Route
            path="whatsapp-templates"
            element={
              <PermissionRoute>
                <WhatsAppTemplates />
              </PermissionRoute>
            }
          />
          <Route
            path="whatsapp-templates/create"
            element={
              <PermissionRoute>
                <WhatsAppTemplateFormPage />
              </PermissionRoute>
            }
          />
          <Route
            path="whatsapp-templates/edit/:id"
            element={
              <PermissionRoute>
                <WhatsAppTemplateFormPage />
              </PermissionRoute>
            }
          />
          <Route
            path="whatsapp-templates/:id"
            element={
              <PermissionRoute>
                <WhatsAppTemplateDetailPage />
              </PermissionRoute>
            }
          />
          <Route
            path="tags"
            element={
              <PermissionRoute>
                <Tags />
              </PermissionRoute>
            }
          />
          <Route
            path="donation-schemas"
            element={
              <PermissionRoute>
                <DonationSchemas />
              </PermissionRoute>
            }
          />
          <Route
            path="reminders"
            element={
              <PermissionRoute>
                <Reminders />
              </PermissionRoute>
            }
          />
          <Route
            path="reminders/new"
            element={
              <PermissionRoute>
                <ReminderDetailPage />
              </PermissionRoute>
            }
          />
          <Route
            path="reminders/:id"
            element={
              <PermissionRoute>
                <ReminderDetailPage />
              </PermissionRoute>
            }
          />
          <Route
            path="trust-masters"
            element={
              <PermissionRoute>
                <TrustMasters />
              </PermissionRoute>
            }
          />
          <Route
            path="trust-masters/new"
            element={
              <PermissionRoute>
                <TrustMasterNewPage />
              </PermissionRoute>
            }
          />
          <Route
            path="trust-masters/:id"
            element={
              <PermissionRoute>
                <TrustMasterDetailPage />
              </PermissionRoute>
            }
          />
          <Route
            path="trust-masters/:id/edit"
            element={
              <PermissionRoute>
                <TrustMasterEditPage />
              </PermissionRoute>
            }
          />
        </Route>
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Router>
  );
}

function App() {
  return (
    <ThemeProvider theme={modernTheme}>
      <CssBaseline />
      <LoadingProvider>
        <GlobalLoader />
        <Toaster
          position="top-center"
          reverseOrder={false}
          gutter={8}
          toastOptions={{
            duration: 3000,
            style: {
              borderRadius: '8px',
              background: '#FFFFFF',
              color: '#000000',
              fontSize: '14px',
              fontWeight: '500',
              padding: '14px 20px',
              boxShadow: '0 4px 12px rgba(90, 106, 207, 0.15)',
              border: '1px solid #E5E7EB',
              fontFamily: '"Rajdhani", sans-serif',
              minWidth: '300px',
            },
          }}
        />
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </LoadingProvider>
    </ThemeProvider>
  );
}

export default App;
